import { useTranslation } from "@sagot/i18n";
import { Field, Input, Select } from "@sagot/ui";
import { useContext } from "react";
import { OptionSelect } from "../builder/action-forms";
import { CardTimeout } from "../builder/card-forms";
import { ExitsEditor } from "../builder/exits-editor";
import type { FlowContext } from "../builder/flow-context";
import { RunVariablesContext } from "../builder/hook-model";
import { useRefs } from "../builder/refs";
import type { FormProps } from "../builder/webhook-form";
import {
  CATEGORY_MAX,
  CHECK_ORDER_SOURCES,
  LOOKUP_ASSET_SOURCES,
  LOOKUP_CATALOGUE_SOURCES,
  PRODUCTS_MAX,
  QUERY_MAX,
  VISIT_DURATION_DEFAULT,
  VISIT_DURATION_MAX,
  VISIT_DURATION_MIN,
  VISIT_STEP_KINDS,
  VISIT_WINDOWS_MAX,
} from "./orders-model";
import { ServiceExits } from "./service-fields";
import type {
  CheckOrderStatusStep,
  CreateOrderStep,
  LookupAssetStep,
  LookupCatalogueStep,
  ScheduleVisitStep,
  ShowCatalogueStep,
  ShowOrderStatusStep,
  VisitStepKind,
} from "./service-types";

const F = "engage.workflows.service.fields";
const RUN_VARIABLE = /^var\.[a-z0-9_]{1,40}$/;

type Flow = Pick<FlowContext, "paths" | "canAddPath" | "facing">;
type FacingFlow = Pick<FlowContext, "paths" | "canAddPath">;

interface VariableFieldProps {
  id: string;
  label: string;
  hint: string;
  value: string | undefined;
  disabled: boolean;
  onChange: (variable: string | undefined) => void;
}

/** A run variable that holds a value the step reads (`var.serial`): typed, or picked from the ones earlier steps made. */
function VariableField({ id, label, hint, value, disabled, onChange }: VariableFieldProps) {
  const { t } = useTranslation();
  const known = useContext(RunVariablesContext);
  const typed = value ?? "";
  const invalid = typed !== "" && !RUN_VARIABLE.test(typed);
  return (
    <Field label={label} hint={hint} error={invalid ? t(`${F}.variableInvalid`) : undefined}>
      <Input list={`${id}-variables`} maxLength={45} disabled={disabled} value={typed} invalid={invalid} onChange={(e) => { onChange(e.target.value.trim() || undefined); }} />
      <datalist id={`${id}-variables`}>{known.map((name) => <option key={name} value={name} />)}</datalist>
    </Field>
  );
}

/** Look up asset: the asset of the client's organisation that the event, the ticket or a typed tag / serial number names. */
export function LookupAssetForm({ step, disabled, onChange, ...flow }: FormProps<LookupAssetStep> & Flow) {
  const { t } = useTranslation();
  const source = step.source ?? "event";
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.lookupAssetHelp`)}</p>
      <Field label={t(`${F}.assetSource`)} hint={t(`${F}.assetSourceHints.${source}`)}>
        <Select value={source} disabled={disabled} onChange={(e) => { const next = e.target.value as NonNullable<LookupAssetStep["source"]>; onChange({ ...step, source: next, variable: next === "variable" ? step.variable : undefined }); }}>
          {LOOKUP_ASSET_SOURCES.map((option) => <option key={option} value={option}>{t(`${F}.assetSources.${option}`)}</option>)}
        </Select>
      </Field>
      {source === "variable" ? <VariableField id={step.id} label={t(`${F}.assetVariable`)} hint={t(`${F}.assetVariableHint`)} value={step.variable} disabled={disabled} onChange={(variable) => { onChange({ ...step, variable }); }} /> : null}
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

const numberOr = (raw: string, fallback: number, min: number, max: number): number => {
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? fallback : Math.min(max, Math.max(min, parsed));
};

/** Schedule visit: proposes up to three windows for the ticket; the client always chooses. */
export function ScheduleVisitForm({ step, disabled, onChange, ...flow }: FormProps<ScheduleVisitStep> & Flow) {
  const { t } = useTranslation();
  const { members } = useRefs();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.scheduleVisitHelp`)}</p>
      <Field label={t(`${F}.visitKind`)}>
        <Select value={step.kind ?? "corrective"} disabled={disabled} onChange={(e) => { onChange({ ...step, kind: e.target.value as VisitStepKind }); }}>
          {VISIT_STEP_KINDS.map((kind) => <option key={kind} value={kind}>{t(`${F}.visitKinds.${kind}`)}</option>)}
        </Select>
      </Field>
      <Field label={t(`${F}.visitWindows`)} hint={t(`${F}.visitWindowsHint`, { max: VISIT_WINDOWS_MAX })}>
        <Input type="number" min={1} max={VISIT_WINDOWS_MAX} disabled={disabled} value={step.windows ?? VISIT_WINDOWS_MAX} onChange={(e) => { onChange({ ...step, windows: numberOr(e.target.value, VISIT_WINDOWS_MAX, 1, VISIT_WINDOWS_MAX) }); }} />
      </Field>
      <Field label={t(`${F}.visitDuration`)} hint={t(`${F}.visitDurationHint`, { min: VISIT_DURATION_MIN, max: VISIT_DURATION_MAX })}>
        <Input type="number" min={VISIT_DURATION_MIN} max={VISIT_DURATION_MAX} disabled={disabled} value={step.duration_minutes ?? VISIT_DURATION_DEFAULT} onChange={(e) => { onChange({ ...step, duration_minutes: numberOr(e.target.value, VISIT_DURATION_DEFAULT, VISIT_DURATION_MIN, VISIT_DURATION_MAX) }); }} />
      </Field>
      <OptionSelect label={t(`${F}.visitEngineer`)} value={step.engineer_user_id} options={members} disabled={disabled} empty={t(`${F}.visitEngineerDefault`)} onChange={(engineer_user_id) => { onChange({ ...step, engineer_user_id }); }} />
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

/** Look up catalogue: up to five products the client's organisation may see, into `var.products`. */
export function LookupCatalogueForm({ step, disabled, onChange, ...flow }: FormProps<LookupCatalogueStep> & Flow) {
  const { t } = useTranslation();
  const source = step.source ?? "query";
  const setSource = (next: NonNullable<LookupCatalogueStep["source"]>) => {
    onChange({ ...step, source: next, query: next === "query" ? (step.query ?? "") : undefined, variable: next === "variable" ? step.variable : undefined });
  };
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.lookupCatalogueHelp`)}</p>
      <Field label={t(`${F}.catalogueSource`)} hint={t(`${F}.catalogueSourceHints.${source}`)}>
        <Select value={source} disabled={disabled} onChange={(e) => { setSource(e.target.value as NonNullable<LookupCatalogueStep["source"]>); }}>
          {LOOKUP_CATALOGUE_SOURCES.map((option) => <option key={option} value={option}>{t(`${F}.catalogueSources.${option}`)}</option>)}
        </Select>
      </Field>
      {source === "query" ? (
        <Field label={t(`${F}.catalogueQuery`)} hint={t(`${F}.catalogueQueryHint`)}>
          <Input maxLength={QUERY_MAX} disabled={disabled} value={step.query ?? ""} onChange={(e) => { onChange({ ...step, query: e.target.value }); }} />
        </Field>
      ) : null}
      {source === "variable" ? <VariableField id={step.id} label={t(`${F}.catalogueVariable`)} hint={t(`${F}.catalogueVariableHint`)} value={step.variable} disabled={disabled} onChange={(variable) => { onChange({ ...step, variable }); }} /> : null}
      <Field label={t(`${F}.catalogueCategory`)} hint={t(`${F}.catalogueCategoryHint`)}>
        <Input maxLength={CATEGORY_MAX} disabled={disabled} value={step.category ?? ""} onChange={(e) => { onChange({ ...step, category: e.target.value || undefined }); }} />
      </Field>
      <Field label={t(`${F}.catalogueLimit`)} hint={t(`${F}.catalogueLimitHint`, { max: PRODUCTS_MAX })}>
        <Input type="number" min={1} max={PRODUCTS_MAX} disabled={disabled} value={step.limit ?? PRODUCTS_MAX} onChange={(e) => { onChange({ ...step, limit: numberOr(e.target.value, PRODUCTS_MAX, 1, PRODUCTS_MAX) }); }} />
      </Field>
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

/** Check order status: the status of the order the event names, a variable holds, or the client ordered last. */
export function CheckOrderStatusForm({ step, disabled, onChange, ...flow }: FormProps<CheckOrderStatusStep> & Flow) {
  const { t } = useTranslation();
  const source = step.source ?? "event";
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.checkOrderHelp`)}</p>
      <Field label={t(`${F}.orderSource`)} hint={t(`${F}.orderSourceHints.${source}`)}>
        <Select value={source} disabled={disabled} onChange={(e) => { const next = e.target.value as NonNullable<CheckOrderStatusStep["source"]>; onChange({ ...step, source: next, variable: next === "variable" ? step.variable : undefined }); }}>
          {CHECK_ORDER_SOURCES.map((option) => <option key={option} value={option}>{t(`${F}.orderSources.${option}`)}</option>)}
        </Select>
      </Field>
      {source === "variable" ? <VariableField id={step.id} label={t(`${F}.orderVariable`)} hint={t(`${F}.orderVariableHint`)} value={step.variable} disabled={disabled} onChange={(variable) => { onChange({ ...step, variable }); }} /> : null}
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

/** Show catalogue: product cards for a search; the client may add one to the cart. Nothing is ordered. */
export function ShowCatalogueForm({ step, disabled, onChange, paths, canAddPath }: FormProps<ShowCatalogueStep> & FacingFlow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.showCatalogueHelp`)}</p>
      <Field label={t(`${F}.catalogueQuery`)} hint={t(`${F}.showCatalogueQueryHint`)}>
        <Input maxLength={QUERY_MAX} disabled={disabled} value={step.query ?? ""} onChange={(e) => { onChange({ ...step, query: e.target.value || undefined }); }} />
      </Field>
      <Field label={t(`${F}.catalogueCategory`)} hint={t(`${F}.catalogueCategoryHint`)}>
        <Input maxLength={CATEGORY_MAX} disabled={disabled} value={step.category ?? ""} onChange={(e) => { onChange({ ...step, category: e.target.value || undefined }); }} />
      </Field>
      <CardTimeout legend={t(`${F}.catalogueTimeout`)} hint={t(`${F}.catalogueTimeoutHint`)} value={step.closed_after} disabled={disabled} onChange={(closed_after) => { onChange({ ...step, closed_after }); }} />
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}

/** Create order: the review of the client's cart with Confirm and Cancel. Only the client's own tap on Confirm places an order. */
export function CreateOrderForm({ step, disabled, onChange, paths, canAddPath }: FormProps<CreateOrderStep> & FacingFlow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.createOrderHelp`)}</p>
      <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" data-testid="create-order-never-silent">{t(`${F}.createOrderSafe`)}</p>
      <CardTimeout legend={t(`${F}.orderTimeout`)} hint={t(`${F}.orderTimeoutHint`)} value={step.cancelled_after} disabled={disabled} onChange={(cancelled_after) => { onChange({ ...step, cancelled_after }); }} />
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}

/** Show order status: the signed-in client's recent orders and their statuses (five at most). */
export function ShowOrderStatusForm({ step, disabled, onChange, paths, canAddPath }: FormProps<ShowOrderStatusStep> & FacingFlow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.showOrderStatusHelp`)}</p>
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}
